import { useCallback, useEffect, useState } from "react";
import { authApi, problemMessage } from "@/api/client";
import type { TenantUser } from "@/api/types";

export type TenantUsersStatus = "loading" | "ready" | "error";

export interface UseTenantUsersResult {
  /** `null` until resolved and on failure — never `[]`, which would be the different and
   *  impossible answer "this farm has no logins at all". */
  users: TenantUser[] | null;
  status: TenantUsersStatus;
  error: string | null;
  /** The id currently being changed, so one row can show progress without disabling the
   *  whole list. `null` when idle. */
  pendingId: string | null;
  /** Set when the server REFUSED a change (409) — "you cannot switch off your own login",
   *  "this is the last owner". Distinct from `error`, which means the list itself failed
   *  to load: one is a screen that cannot show anything, the other is a screen that is
   *  fine and an action that was declined. */
  actionError: string | null;
  setActive: (userId: string, active: boolean) => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * The owner's access list — who can sign in to this farm, and the control to switch a
 * login off or back on.
 *
 * <p>
 * Owner-only server-side (403 for an employee login). This hook does not check the role
 * and must not: a client-side role check is a rendering decision, and treating it as the
 * control is how the real one stops being maintained.
 * </p>
 *
 * <p>
 * Re-reads the whole list after every change rather than mutating the row locally. The
 * server may have refused the change (409), may have applied it to a row this client's
 * copy is stale about, and — in the revoke case — has also destroyed sessions. Patching
 * one field client-side would leave the screen asserting an outcome it did not observe,
 * which is the same class of mistake as a test asserting on its own return value.
 * </p>
 */
export function useTenantUsers(): UseTenantUsersResult {
  const [users, setUsers] = useState<TenantUser[] | null>(null);
  const [status, setStatus] = useState<TenantUsersStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await authApi.listUsers(signal);
      if (signal?.aborted) return;
      setUsers(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setUsers(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load who has access to this farm."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const setActive = useCallback(
    async (userId: string, active: boolean): Promise<void> => {
      setPendingId(userId);
      setActionError(null);
      try {
        if (active) {
          await authApi.restoreUser(userId);
        } else {
          await authApi.revokeUser(userId);
        }
        await load();
      } catch (err) {
        // The server's own words. Its 409s say exactly which lock-out this would have
        // caused ("this is the last owner login"), and a generic "something went wrong"
        // would throw away the only part the reader needs.
        setActionError(
          problemMessage(err, active ? "Could not restore this login." : "Could not switch off this login."),
        );
        // Re-read anyway: a failure here may still have left the list stale.
        await load();
      } finally {
        setPendingId(null);
      }
    },
    [load],
  );

  return {
    users,
    status,
    error,
    pendingId,
    actionError,
    setActive,
    reload: () => load(),
  };
}
